import CheckoutCart from "@/components/CheckoutCart/CheckoutCart";
import { CheckoutForm } from "@/components/CheckoutForm/CheckoutForm";
import { CheckoutApiResponse } from "@/api/stripe.api";
import { useCheckoutContext } from "@/components/CheckoutForm/utils";
import { LoginModalProvider } from "@/components/LoginModal/LoginModalProvider";
import { useLoginModal } from "@/components/LoginModal/useLoginModal";
import Navbar from "@/components/Navbar/Navbar";
import { UploadModalProvider } from "@/components/UploadModal/UploadModalProvider";
import checkoutLoader, {
  CheckoutLoaderCheckoutData,
  transformCheckoutResponse,
} from "@/pages/Checkout/checkoutLoader";
import { $account } from "@/stores/account";
import { MissingSourceFile } from "@/utils/checkSourceFiles";
import { Cart } from "@/utils/types/types";
import { WebUser } from "@prisma/client";
import { useStore } from "@nanostores/react";
import { useMediaQuery } from "@mantine/hooks";
import { CheckoutProvider } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLoaderData, useRevalidator } from "react-router-dom";

// Content blockers / Safari private browsing can block js.stripe.com — a bare
// loadStripe rejection here is an unhandled crash (Sentry 1T). Resolve to null
// and show the banner below instead of a silent dead checkout.
// GA migration 2026-08-25: beta flag removed — Elements with Checkout
// Sessions is GA at stripe-js 7 / API 2025-03-31.basil.
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PKEY || "").catch(error => {
  console.error("[stripe] loadStripe failed", error);
  (window as any).Sentry?.captureException(error);
  return null;
});

function CheckoutContent({
  cart,
  checkout,
  user,
  cartDimensions,
  onCheckoutReplaced,
}: {
  cart: Cart;
  checkout: CheckoutLoaderCheckoutData;
  user?: WebUser;
  cartDimensions: { width: number; height: number } | undefined;
  onCheckoutReplaced: (nextCheckout: CheckoutApiResponse) => void;
}) {
  const account = useStore($account);
  const isMobile = useMediaQuery("(max-width: 767px)"); // below md breakpoint
  const loginModal = useLoginModal();
  const revalidator = useRevalidator();
  const prevIsLoggedInRef = useRef(account.isLoggedIn);

  const ctx = useCheckoutContext({
    session: checkout.session,
    shippingOptions: checkout.shippingOptions,
    shippingPickupOption: checkout.shippingPickupOption,
    shippingOversizedOption: checkout.shippingOversizedOption,
    user,
    cartDimensions,
    cart,
    onCheckoutReplaced,
    priceComparison: checkout.priceComparison,
  });

  useEffect(() => {
    const hasSeenModal = localStorage.getItem("checkout_login_modal_seen");

    if (!account.isLoggedIn && !hasSeenModal) {
      localStorage.setItem("checkout_login_modal_seen", "true");
      loginModal.open({
        continueAsGuest: true,
      });
    }
  }, [account.isLoggedIn, loginModal]);

  useEffect(() => {
    if (account.isLoggedIn) {
      localStorage.removeItem("checkout_login_modal_seen");
    }
  }, [account.isLoggedIn]);

  useEffect(() => {
    const justLoggedIn = !prevIsLoggedInRef.current && account.isLoggedIn;
    if (justLoggedIn) {
      revalidator.revalidate();
    }
    prevIsLoggedInRef.current = account.isLoggedIn;
  }, [account.isLoggedIn, revalidator]);

  return (
    <UploadModalProvider>
      <div className="flex h-full flex-col" data-clarity-mask="true">
        <Navbar checkout />
        <div className="md:grid md:h-[calc(100vh-62px)] md:grid-cols-2 md:bg-[linear-gradient(90deg,#fff_50%,_#fafafa_50%)]">
          {isMobile && cart && <CheckoutCart cart={cart} ctx={ctx} />}
          <div className="flex w-full min-w-0 flex-col md:ml-auto md:max-w-[780px]">
            <CheckoutForm ctx={ctx} />
            {/* Slot where CheckoutCart portals the CONFIRM AND PAY button on
                mobile, so it flows directly below the accordion. */}
            {isMobile && <div id="mobile-confirm-slot" />}
          </div>

          {!isMobile && cart && <CheckoutCart cart={cart} ctx={ctx} />}
        </div>
      </div>
    </UploadModalProvider>
  );
}

export function Checkout() {
  const loaderData = useLoaderData(checkoutLoader) as {
    cart: Cart;
    checkout: CheckoutLoaderCheckoutData;
    user?: WebUser;
    cartDimensions?: { width: number; height: number };
    missingSourceFiles?: MissingSourceFile[];
  };
  const [checkout, setCheckout] = useState(loaderData.checkout);
  const [stripeLoadFailed, setStripeLoadFailed] = useState(false);

  useEffect(() => {
    setCheckout(loaderData.checkout);
  }, [loaderData.checkout]);

  useEffect(() => {
    stripePromise.then(stripe => {
      if (!stripe) setStripeLoadFailed(true);
    });
  }, []);

  const handleCheckoutReplaced = useCallback(
    (nextCheckout: CheckoutApiResponse) => {
      if (typeof window !== "undefined") {
        // Persist the intended post-finalization step so CheckoutForm restores
        // directly to Payment after CheckoutProvider remounts.
        sessionStorage.setItem("checkout_pending_next_step", "4");
        sessionStorage.setItem("checkout_active_step", "4");
        sessionStorage.setItem("checkout_max_passed_step", "4");
        sessionStorage.setItem("checkout_collapse_panel", "false");
        sessionStorage.setItem("checkout_review_completed", "false");
      }
      setCheckout(transformCheckoutResponse(nextCheckout));
    },
    []
  );

  // Source-file existence guard (T-50765): one or more cart items reference
  // an original file that no longer exists in storage. Paying now would
  // produce an order the studio cannot print (jobbuilder imports 403, no zip,
  // incomplete order folder) — block payment and ask for a re-upload while
  // the customer is still here. The check is fail-open, so reaching this
  // branch means the CDN definitively answered 403/404 for these files.
  const missingFiles = loaderData.missingSourceFiles || [];
  if (missingFiles.length > 0) {
    return (
      <LoginModalProvider>
        <div className="flex h-full flex-col" data-clarity-mask="true">
          <Navbar checkout />
          <div className="mx-auto mt-16 max-w-[560px] px-6 text-center">
            <h2 className="mb-3 text-2xl font-semibold">
              We can&apos;t find the original file for{" "}
              {missingFiles.length === 1 ? "one of your items" : "some of your items"}
            </h2>
            <p className="mb-4 text-neutral-700">
              This usually happens when an order is picked up again after a long
              time and the uploaded artwork has since been removed. To make
              sure we can print your order, please re-upload the artwork and
              add the item to your cart again:
            </p>
            <ul className="mb-6 list-none text-neutral-800">
              {missingFiles.map(f => (
                <li key={f.url} className="mb-1 font-medium">
                  {f.fileName}
                </li>
              ))}
            </ul>
            <a
              href="/upload"
              className="inline-block rounded-lg bg-[#4AA0A0] px-6 py-3 font-semibold text-white no-underline"
            >
              Re-upload artwork
            </a>
            <p className="mt-6 text-sm text-neutral-500">
              Need a hand? Call the studio at 212-966-1976 or email
              info@tribecaprintworks.com — we&apos;re happy to help.
            </p>
          </div>
        </div>
      </LoginModalProvider>
    );
  }

  return (
    <LoginModalProvider>
      {stripeLoadFailed && (
        <div className="bg-red-50 px-4 py-3 text-center text-sm text-red-700">
          Our payment system couldn&apos;t load — please turn off content
          blockers or leave private browsing, then refresh this page.
        </div>
      )}
      <CheckoutProvider
        key={checkout.session.id || checkout.session.client_secret}
        stripe={stripePromise}
        options={{
          // GA API: fetchClientSecret replaces clientSecret (beta_6+).
          fetchClientSecret: () =>
            Promise.resolve(checkout.session.client_secret),
          elementsOptions: {
            // Saved-cards redisplay: pre-Clover defaults are "never", so the
            // wallet never shows at checkout unless set explicitly
            // (docs.stripe.com/js/custom_checkout/init).
            savedPaymentMethod: {
              enableRedisplay: "auto",
              enableSave: "auto",
            },
            appearance: {
              theme: "stripe",
              labels: "floating",
              variables: {
                colorPrimary: "#4AA0A0",
                colorBackground: "#fff",
                colorText: "#191919",
                fontFamily: "Afacad",
                borderRadius: "8px",
              },
              rules: {
                ".Label": {
                  fontFamily: "Afacad",
                },
                ".Label--floating": {
                  fontSize: "12px",
                  color: "#606666b2",
                },
                ".Input": {
                  fontSize: "16px",
                  fontFamily: "Afacad",
                },
              },
            },
            fonts: [
              {
                cssSrc: `https://fonts.googleapis.com/css2?family=Afacad:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500;1,600;1,700&display=swap`,
              },
            ],
          },
        }}
      >
        <CheckoutContent
          cart={loaderData.cart}
          checkout={checkout}
          user={loaderData.user}
          cartDimensions={loaderData.cartDimensions}
          onCheckoutReplaced={handleCheckoutReplaced}
        />
      </CheckoutProvider>
    </LoginModalProvider>
  );
}
